/**
 * mSTEM project CRUD wrapper. Thin layer on top of mstem-storage.js for the
 * Phase 3 `mstem.myProjects[]` localStorage slice. All Phase 3 modals call
 * into this helper rather than touching localStorage directly.
 *
 * Security note (T-03-01-01 + T-03-01-04):
 *   - Inherits shape validation from mstem-storage.readJsonList (rejects
 *     entries missing id+name string fields).
 *   - Per-entry vmState size capped at VM_STATE_MAX_BYTES (500_000 chars)
 *     to avoid QuotaExceededError on writes. Caller receives the error via
 *     optional onQuotaError callback rather than a silent crash.
 */

import {
    readMyProjects as readListFromStorage,
    writeMyProjects as writeListToStorage,
    readSeedHash,
    writeSeedHash
} from './mstem-storage';

const VM_STATE_MAX_BYTES = 500000;
const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const ID_LENGTH = 8;

const generateProjectId = function () {
    let id = '';
    for (let i = 0; i < ID_LENGTH; i++) {
        id += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)];
    }
    return id;
};

const nowIso = function () {
    return new Date().toISOString();
};

const readMyProjects = function () {
    return readListFromStorage();
};

const writeMyProjects = function (list, onQuotaError) {
    // Pre-check vmState size on every entry to surface quota issues before
    // attempting the actual localStorage write. Returns false on rejection
    // so callers can branch (e.g. show "không thể lưu" toast).
    if (Array.isArray(list)) {
        for (let i = 0; i < list.length; i++) {
            const entry = list[i];
            if (entry && typeof entry.vmState === 'string' &&
                entry.vmState.length > VM_STATE_MAX_BYTES) {
                if (onQuotaError) {
                    onQuotaError(new Error('vmState exceeds VM_STATE_MAX_BYTES'));
                }
                return false;
            }
        }
    }
    try {
        writeListToStorage(list);
        return true;
    } catch (e) {
        if (onQuotaError) onQuotaError(e);
        return false;
    }
};

const createMyProject = function (input) {
    const safe = input || {};
    return {
        id: generateProjectId(),
        name: typeof safe.name === 'string' ? safe.name : '',
        type: typeof safe.type === 'string' ? safe.type : 'none',
        description: typeof safe.description === 'string' ? safe.description : '',
        createdAt: nowIso(),
        updatedAt: nowIso(),
        vmState: null,
        isMock: false
    };
};

const updateMyProject = function (entry, patch) {
    return Object.assign({}, entry, patch, {updatedAt: nowIso()});
};

const getMyProjectById = function (list, id) {
    if (!Array.isArray(list) || typeof id !== 'string') return null;
    for (let i = 0; i < list.length; i++) {
        if (list[i] && list[i].id === id) return list[i];
    }
    return null;
};

const deleteMyProject = function (list, id) {
    if (!Array.isArray(list) || typeof id !== 'string') return list;
    return list.filter(entry => entry.id !== id);
};

// Phase 5 / Plan 05-01 (Assumption A2): seed version bumped v1 → v2 so users who
// already booted Phase 3 (seedHash='v1') re-run the seed once and receive the 3
// new SmartHome scenarios. The seed in app-state-hoc MERGES new mock entries by
// id rather than overwriting, so a v1 user's saved projects are preserved.
const isSeeded = function () {
    return readSeedHash() === 'v2';
};

const markSeeded = function () {
    writeSeedHash('v2');
};

// Re-export the storage key constants so callers can import them from
// mstem-project-storage.js without reaching into mstem-storage.js.
// NOTE (W-5 fix): JavaScript requires the `export … from` syntax for
// re-exports. Do NOT list them in the local `export {}` block below — they
// are not declared locally (only imported), which would cause a
// ReferenceError at module load.
export {MY_PROJECTS_KEY, SEED_HASH_KEY} from './mstem-storage';

export {
    VM_STATE_MAX_BYTES,
    generateProjectId,
    readMyProjects,
    writeMyProjects,
    createMyProject,
    updateMyProject,
    getMyProjectById,
    deleteMyProject,
    isSeeded,
    markSeeded
};
